import React from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Text,
  IconButton,
  Tooltip,
  Button,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Card,
  CardBody,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from '@chakra-ui/react';
import { DeleteIcon, RepeatIcon, TimeIcon, EditIcon } from '@chakra-ui/icons';
import HeaderMapping from './HeaderMapping';
import { sheetSourceService } from '../../services/sheetSource.service';
import { format, isValid, parseISO } from 'date-fns';

const formatDate = (dateString, formatStr = 'PPpp') => {
  if (!dateString) return 'N/A';
  try {
    // Parse ISO string to Date object
    const date = parseISO(dateString);
    return isValid(date) ? format(date, formatStr) : 'Invalid date';
  } catch (error) {
    console.error('Date parsing error:', error);
    return 'Invalid date';
  }
};

const SheetList = ({ sheets, selectedSheet, onSelect, onDelete, onRefresh, onUpdateMapping, loading, constants }) => {
  const [deleteDialog, setDeleteDialog] = React.useState({ open: false, sheet: null });
  const [historyDialog, setHistoryDialog] = React.useState({ open: false, sheet: null });
  const [mappingDialog, setMappingDialog] = React.useState({ open: false, sheet: null });

  const handleDelete = async () => {
    if (deleteDialog.sheet) {
      await onDelete({
        sheetId: deleteDialog.sheet.sheetId,
        gid: deleteDialog.sheet.gid
      });
      setDeleteDialog({ open: false, sheet: null });
    }
  };

  const handleRefresh = async (sheet) => {
    if (onRefresh) {
      await onRefresh(sheet);
    }
  };

  const renderFetchHistory = (history) => {
    if (!history || history.length === 0) return 'No fetch history';

    // Sort history to show latest entries first
    const sortedHistory = [...history].sort((a, b) => 
      new Date(b.fetchedAt) - new Date(a.fetchedAt)
    );

    return (
      <Accordion allowToggle>
        <AccordionItem>
          <AccordionButton>
            <Box flex="1" textAlign="left">
              <HStack>
                <Text>Fetch History</Text>
                <Badge colorScheme="blue">{history.length} Fetches</Badge>
              </HStack>
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel>
            <VStack spacing={2} align="stretch">
              {sortedHistory.map((entry, index) => (
                <Card key={index} variant="outline" size="sm">
                  <CardBody p={3}>
                    <HStack justify="space-between" mb={2}>
                      <Text fontWeight="bold" fontSize="sm">
                        {formatDate(entry.fetchedAt, 'PPpp')}
                      </Text>
                      <Badge colorScheme={entry.status === 'success' ? 'green' : 'red'}>
                        {entry.status}
                      </Badge>
                    </HStack>
                    <HStack spacing={4} fontSize="sm" color="gray.600">
                      <Text>Rows: {entry.rowCount || 0}</Text>
                      {entry.headers && entry.headers.length > 0 && (
                        <Tooltip label={entry.headers.join(', ')} hasArrow>
                          <Text cursor="help">
                            Headers: {entry.headers.length}
                          </Text>
                        </Tooltip>
                      )}
                    </HStack>
                  </CardBody>
                </Card>
              ))}
            </VStack>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    );
  };

  return (
    <Box>
      <Card mb={4}>
        <CardBody>
          <HStack justify="space-between">
            <Heading size="md">Sheet Sources</Heading>
            <Badge colorScheme="blue">{sheets.length} Sheets</Badge>
          </HStack>
        </CardBody>
      </Card>

      {sheets.length === 0 ? (
        <Card variant="outline">
          <CardBody>
            <Text color="gray.500" textAlign="center">
              No sheets added yet
            </Text>
          </CardBody>
        </Card>
      ) : (
        <VStack spacing={4} align="stretch">
          {sheets.map((sheet) => (
            <Card
              key={`${sheet.sheetId}-${sheet.gid}`}
              variant="outline"
              cursor="pointer"
              bg={selectedSheet?.sheetId === sheet.sheetId && selectedSheet?.gid === sheet.gid ? 'blue.50' : 'white'}
              onClick={() => onSelect(sheet)}
              _hover={{ bg: 'gray.50' }}
              opacity={loading ? 0.7 : 1}
            >
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <HStack justify="space-between">
                    <VStack align="start" spacing={2}>
                      <Text fontWeight="bold">{sheet.name || sheet.sheetId}</Text>
                      <HStack spacing={2} wrap="wrap">
                        <Badge colorScheme="blue">GID: {sheet.gid || '0'}</Badge>
                        <Badge colorScheme="green">{(sheet.headerList || []).length} Headers</Badge>
                        <Badge colorScheme="purple">
                          Updated: {formatDate(sheet.lastFetchedAt, 'PP')}
                        </Badge>
                      </HStack>
                    </VStack>

                    <HStack spacing={2}>
                      <Tooltip label="Edit Mapping" hasArrow>
                        <IconButton
                          icon={<EditIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setMappingDialog({ open: true, sheet });
                          }}
                          isDisabled={loading}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                        />
                      </Tooltip>
                      <Tooltip label="View History" hasArrow>
                        <IconButton
                          icon={<TimeIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setHistoryDialog({ open: true, sheet });
                          }}
                          isDisabled={loading}
                          size="sm"
                          variant="ghost"
                        />
                      </Tooltip>
                      <Tooltip label="Refresh Data" hasArrow>
                        <IconButton
                          icon={<RepeatIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRefresh(sheet);
                          }}
                          isDisabled={loading}
                          size="sm"
                          variant="ghost"
                        />
                      </Tooltip>
                      <Tooltip label="Delete" hasArrow>
                        <IconButton
                          icon={<DeleteIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteDialog({ open: true, sheet });
                          }}
                          isDisabled={loading}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                        />
                      </Tooltip>
                    </HStack>
                  </HStack>

                  {/* Fetch History */}
                  {sheet.fetchHistory && sheet.fetchHistory.length > 0 && (
                    <Box>
                      {renderFetchHistory(sheet.fetchHistory)}
                    </Box>
                  )}
                </VStack>
              </CardBody>
            </Card>
          ))}
        </VStack>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={deleteDialog.open}
        leastDestructiveRef={undefined}
        onClose={() => setDeleteDialog({ open: false, sheetId: null })}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Sheet
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete this sheet? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                onClick={() => setDeleteDialog({ open: false, sheet: null })}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={() => handleDelete()}
                ml={3}
                isLoading={loading}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Header Mapping Modal */}
      <Modal
        isOpen={mappingDialog.open}
        onClose={() => setMappingDialog({ open: false, sheet: null })}
        size="6xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Configure Header Mapping</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {mappingDialog.sheet && (
              <HeaderMapping
                sheet={mappingDialog.sheet}
                onUpdateMapping={onUpdateMapping}
                loading={loading}
                constants={constants}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SheetList;
